import React from 'react'

import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcButton,
  DcDiv,
  DcDropDown,
  DcSwitch,
  PixelAlert,
  PixelFactoryContext,
  PixelFlexBox,
  PixelHeaderPanel,
  PixelText,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/internal/ajax/AjaxResponse'
import { post } from 'utils/api-routes.util'

import { IPixelFactory } from '../../../../../../decl.d'
import { ICommunicationConfig } from '../SMSSubecription'

interface IProps {
  handleClose?: (_action?: string, _data?: ICommunicationConfig) => void
  origin: 'tabs' | 'popup'
  data: ISetting
}
interface ISetting {
  ivr_confirm_status: string
  ivr_cancel_status: string
  auto_status_update: boolean
  auto_notify?: boolean
  is_retry: boolean | null
}
const IVRSetting = (props: IProps) => {
  const { origin, data, handleClose } = props
  const { showSuccess, showFailure, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [setting, setSetting] = React.useState<ISetting>({
    ivr_confirm_status: '',
    ivr_cancel_status: '',
    auto_status_update: true,
    is_retry: null,
  })
  const [error, setError] = React.useState({
    error: '',
    field: '',
  })
  React.useEffect(() => {
    setSetting({
      ivr_confirm_status: data.ivr_confirm_status,
      ivr_cancel_status: data.ivr_cancel_status,
      auto_status_update:
        data.auto_status_update === null ? false : data.auto_status_update,
      is_retry: data.is_retry === null ? false : data.is_retry,
    })
  }, [data])
  const { state } = React.useContext(OrdersContext)
  const autoStatusUpdate = (value) => {
    showPageLoader()
    post('/communication/config/auto-status-update', {
      auto_status_update: value,
    }).subscribe((res: AjaxResponse<ICommunicationConfig | any>) => {
      hidePageLoader()
      if (res.response.data) {
        setSetting({
          ivr_cancel_status: res.response.data?.ivr_cancel_status,
          ivr_confirm_status: res.response.data?.ivr_confirm_status,
          auto_status_update: res.response.data?.auto_status_update ?? false,
          is_retry: res.response.data?.is_retry ?? false,
        })
        showSuccess('Successfully enabled to auto status update')
      } else if (res.response.error || res.response.field) {
        setError({
          error: res.response.error,
          field: res.response.field,
        })
      } else {
        showFailure(res.response.error)
      }
    })
  }
  const statusUpdate = (data) => {
    showPageLoader()
    post('/communication/config/ivr-statuses', data).subscribe((res: any) => {
      hidePageLoader()
      const response = res.response
      if (response.data) {
        setSetting({
          ivr_cancel_status: response.data?.ivr_cancel_status,
          ivr_confirm_status: response.data?.ivr_confirm_status,
          auto_status_update: response.data?.auto_status_update,
          is_retry: response.data?.is_retry ?? false,
        })
        showSuccess('Successfully enabled to auto status update')
        if (origin === 'popup') {
          handleClose('success', response.data)
        }
      } else if (response.error || response.field) {
        setError({
          error: response.error,
          field: response.field,
        })
      } else {
        showFailure(response.error)
      }
      if (origin === 'popup') {
        handleClose()
      }
    })
  }
  return (
    <PixelFlexBox
      flexDirection="column"
      justifyContent="flex-start"
      gap="10px"
      padding={origin === 'popup' ? '0px' : '20px'}
      width={origin === 'popup' ? '100%' : '50%'}
      style={{ cursor: data.auto_notify ? 'auto' : 'not-allowed' }}
    >
      <PixelAlert>
        The order status will be automatically updated when the customer
        confirms or cancels their order via phone call(IVR).
      </PixelAlert>
      <PixelHeaderPanel>
        <DcDiv style={{ minWidth: '220px' }}>
          <PixelText
            textSize="20px"
            style={{ fontWeight: 'bold' }}
            customColor="#2f3c4d"
            margin="0px 0px 15px 0px"
          >
            Auto Status Update
          </PixelText>
        </DcDiv>
        <DcSwitch
          className={'large'}
          disabled={!data?.auto_notify}
          value={setting.auto_status_update}
          onChange={(e) => {
            autoStatusUpdate(e.target.checked)
          }}
          checked={setting.auto_status_update}
          label={setting.auto_status_update}
        />
      </PixelHeaderPanel>
      <PixelFlexBox
        gap="10px"
        flexDirection="column"
        padding="20px"
        style={{
          border: '1px solid gainsboro',
        }}
      >
        <DcDropDown
          required
          disabled={!data?.auto_notify || !setting.auto_status_update}
          error={
            error.field === 'ivr_confirm_status' && error.error !== ''
              ? error.error
              : null
          }
          placeholder="Confirmation Status"
          options={state.allowedStatuses
            .filter((status) => status.ems_status === 'confirmed')
            .map((item) => {
              return {
                label: item.title,
                value: item.title,
              }
            })}
          value={setting.ivr_confirm_status}
          onChange={(e) => {
            if (origin === 'tabs') {
              statusUpdate({
                ivr_confirm_status: e.target.value,
                ivr_cancel_status: setting.ivr_cancel_status,
                is_retry: setting.is_retry,
              })
            } else {
              setSetting({
                ...setting,
                ivr_confirm_status: e.target.value,
              })
            }
          }}
        />
        <DcDropDown
          required
          disabled={!data?.auto_notify || !setting.auto_status_update}
          error={
            error.field === 'ivr_cancel_status' && error.error !== ''
              ? error.error
              : null
          }
          placeholder="Cancellation Status"
          options={state.allowedStatuses
            .filter((status) => status.ems_status === 'cancelled')
            .map((item) => {
              return {
                label: item.title,
                value: item.title,
              }
            })}
          value={setting.ivr_cancel_status}
          onChange={(e) => {
            if (origin === 'tabs') {
              statusUpdate({
                ivr_confirm_status: setting.ivr_confirm_status,
                ivr_cancel_status: e.target.value,
                is_retry: setting.is_retry,
              })
            } else {
              setSetting({
                ...setting,
                ivr_cancel_status: e.target.value,
              })
            }
          }}
        />
        <PixelHeaderPanel>
          <PixelText
            textSize="13px"
            customColor="#2f3c4d"
            margin="0px 0px 0px 0px"
          >
            Retry once when customer <b>Cancel</b> the Order
          </PixelText>
          <DcSwitch
            disabled={!data?.auto_notify || !setting.auto_status_update}
            value={setting.is_retry}
            onChange={(e) => {
              if (origin === 'tabs') {
                statusUpdate({
                  ivr_confirm_status: setting.ivr_confirm_status,
                  ivr_cancel_status: setting.ivr_cancel_status,
                  is_retry: e.target.checked,
                })
              } else {
                setSetting({
                  ...setting,
                  is_retry: e.target.checked,
                })
              }
            }}
            checked={setting.is_retry}
            label={setting.is_retry}
          />
        </PixelHeaderPanel>
      </PixelFlexBox>
      {origin === 'popup' && (
        <PixelFlexBox justifyContent="flex-end" gap="10px">
          <DcButton variant="outline" onClick={handleClose}>
            Close
          </DcButton>
          <DcButton
            onClick={() => {
              statusUpdate({
                ivr_confirm_status: setting.ivr_confirm_status,
                ivr_cancel_status: setting.ivr_cancel_status,
                is_retry: setting.is_retry,
              })
            }}
          >
            Save
          </DcButton>
        </PixelFlexBox>
      )}
    </PixelFlexBox>
  )
}

export default IVRSetting
